import { WATCH_DATA_KEY } from '../config';

interface WatchData {
  date: string;
  seconds: number;
}

export class WatchBudgetStore {
  constructor(
    private readonly storage: Storage = localStorage,
    private maxDailySeconds = 0,
  ) {}

  setMaxDailySeconds(maxDailySeconds: number): void {
    this.maxDailySeconds = maxDailySeconds;
  }

  getTimeLeft(date = new Date()): number {
    return Math.max(0, this.maxDailySeconds - this.load(date).seconds);
  }

  getWatchedSeconds(date = new Date()): number {
    return Math.min(this.maxDailySeconds, this.load(date).seconds);
  }

  increment(date = new Date()): number {
    const data = this.load(date);
    data.seconds = Math.min(this.maxDailySeconds, data.seconds + 1);
    this.save(data);
    return data.seconds;
  }

  reset(date = new Date()): void {
    this.save({ date: this.toDateKey(date), seconds: 0 });
  }

  private load(date: Date): WatchData {
    const today = this.toDateKey(date);

    try {
      const raw = this.storage.getItem(WATCH_DATA_KEY);
      if (!raw) {
        const fresh = { date: today, seconds: 0 };
        this.save(fresh);
        return fresh;
      }

      const parsed = JSON.parse(raw) as Partial<WatchData>;
      if (parsed.date === today && Number.isFinite(parsed.seconds)) {
        return { date: today, seconds: Math.max(0, Number(parsed.seconds)) };
      }
    } catch {
      // Ignore invalid local data and overwrite it with a new day bucket.
    }

    const fresh = { date: today, seconds: 0 };
    this.save(fresh);
    return fresh;
  }

  private save(data: WatchData): void {
    this.storage.setItem(WATCH_DATA_KEY, JSON.stringify(data));
  }

  private toDateKey(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
